import {
  isTerminalJobStatus,
  JOB_TYPES,
  type JobDto,
  type JobLogDto,
  type JobStatus,
} from "@shelflist/shared";
import { queryOptions } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, call } from "./api";

export const POLL_INTERVAL_MS = 2000;

export const jobKeys = {
  all: ["jobs"] as const,
  list: (filters: JobFilters) => ["jobs", "list", filters] as const,
  detail: (id: string) => ["jobs", "detail", id] as const,
  libraries: ["calibre", "libraries"] as const,
  backfill: ["jobs", "koreader-backfill"] as const,
};

export interface JobFilters {
  status?: JobStatus;
  type?: string;
  page: number;
}

export const JOBS_PAGE_SIZE = 25;

/** The jobs list; refreshes while any job on the page is still queued or running. */
export const jobsQuery = (filters: JobFilters) =>
  queryOptions({
    queryKey: jobKeys.list(filters),
    queryFn: () => {
      const query: Record<string, string> = {
        page: String(filters.page),
        pageSize: String(JOBS_PAGE_SIZE),
      };
      if (filters.status) query.status = filters.status;
      if (filters.type) query.type = filters.type;
      return call(api.admin.jobs.$get({ query: query as never }));
    },
    refetchInterval: (query) =>
      query.state.data?.jobs.some((job) => !isTerminalJobStatus(job.status))
        ? POLL_INTERVAL_MS * 2
        : false,
  });

/** One job; polled every 2 s until it reaches a terminal status. */
export const jobQuery = (id: string) =>
  queryOptions({
    queryKey: jobKeys.detail(id),
    queryFn: async () => (await call(api.admin.jobs[":id"].$get({ param: { id } }))).job as JobDto,
    refetchInterval: (query) =>
      query.state.data && isTerminalJobStatus(query.state.data.status) ? false : POLL_INTERVAL_MS,
  });

export const calibreLibrariesQuery = queryOptions({
  queryKey: jobKeys.libraries,
  queryFn: () => call(api.admin.calibre.libraries.$get()),
});

export async function fetchJobLog(
  id: string,
  after: number,
  levels: string | undefined,
): Promise<JobLogDto> {
  const query: Record<string, string> = { after: String(after), limit: "200" };
  if (levels) query.level = levels;
  return (await call(
    api.admin.jobs[":id"].log.$get({ param: { id }, query: query as never }),
  )) as JobLogDto;
}

/** Translated name of a job type; types this version does not know are shown with their raw name. */
export function useJobTypeLabel() {
  const { t } = useTranslation("jobs");
  return (type: string): string =>
    (JOB_TYPES as readonly string[]).includes(type)
      ? t(`types.${type as (typeof JOB_TYPES)[number]}`)
      : t("unknownType", { type });
}

/** Time from start to finish (or to now while running); a dash before the job started. */
export function jobDuration(job: JobDto, formatDuration: (ms: number) => string): string {
  if (!job.startedAt) return "—";
  const end = job.finishedAt ? new Date(job.finishedAt).getTime() : Date.now();
  return formatDuration(end - new Date(job.startedAt).getTime());
}

/** Files without a KOReader digest and the running backfill job (state of the Jobs page action). */
export const backfillStatusQuery = queryOptions({
  queryKey: jobKeys.backfill,
  queryFn: () => call(api.admin.jobs["koreader-digest-backfill"].$get()),
  refetchInterval: (query) => (query.state.data?.activeJobId ? POLL_INTERVAL_MS * 2 : false),
});

export const startDigestBackfill = () => call(api.admin.jobs["koreader-digest-backfill"].$post());
